import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../setupAxios";
import TopBar from "../components/TopBar";
import Badge from "../components/Badge";
import { avisarCambioResumen, onActualizarPanel } from "../statsBus";
import { isoToEs, esToIso } from "../fechas";
import { tonoEstado } from "../estados";
import { useDialog } from "../context/dialogContext";
import { pedirConfirmacionRevision } from "../revision";
import FichaLlaveModal from "../components/FichaLlave";
import InfoLlaveModal from "../components/InfoLlaveModal";
import { usePaginacion, Paginador } from "../components/Paginacion";
import useNavegacionLista from "../components/useNavegacionLista";
import AvisoCuentaPersona, { coincidenciaExacta, cuentaDeResultado } from "../components/AvisoCuentaPersona";

// Fecha de hoy en ISO (huso del navegador), para no permitir devoluciones
// estimadas anteriores a hoy en ningún formulario de esta pantalla.
const hoyIso = () => new Date().toISOString().slice(0, 10);

/*
  Operar.jsx — Panel principal de operación diaria, reconstruido con la misma
  estructura y funcionalidad que el operar.html original:

    · PRESTAR en modo "cesta": buscas piso (por código o ID), tecleas la llave,
      se valida en vivo (cajetín + disponibilidad), eliges persona, lo añades a
      una prelista y confirmas todo junto. Autocompletado de piso y persona
      (personal + residentes); el desplegable "Departamento" solo acota esa
      búsqueda (Personal/Residente/departamento real), no se guarda en el
      préstamo.
    · DEVOLVER por tecleo rápido: piso + código + Enter; muestra de quién era,
      desde cuándo y qué pendientes le quedan a esa persona.
    · LEDGER con filtros por columna, rango de fechas, "solo vencidas",
      detalle de préstamo, edición, comentarios, historial y reclamación.

  Adaptado a los campos del esquema nuevo:
    - "código de llave" = llaves.id_llave (numérico).
    - persona enlazada => { tipo_persona:'personal'|'alumno', id_persona }.
*/

export default function Operar() {
  return (
    <div className="app-shell">
      <TopBar />

      <div className="op-grid">
        <Prestar onCambio={avisarCambioResumen} />
        <Devolver onCambio={avisarCambioResumen} />
      </div>

      <h2 className="section-title">📜 Historial</h2>
      <Ledger />
    </div>
  );
}

/* ======================================================================= */
/*  PRESTAR (cesta)                                                         */
/* ======================================================================= */
function Prestar({ onCambio }) {
  const [buscaCod, setBuscaCod] = useState("");   // buscar piso por código
  const [idPiso, setIdPiso] = useState("");       // ID del piso (numérico)
  const [cod, setCod] = useState("");             // código de llave (numérico)
  const [persona, setPersona] = useState("");
  const [perSel, setPerSel] = useState(null);     // {tipo_persona,id_persona,departamento}
  const [depto, setDepto] = useState("");
  const [dev, setDev] = useState("");
  const [tipoPrestamo, setTipoPrestamo] = useState("provisional");
  // "Preparar" (desactivado por defecto): las llaves que se añadan con él
  // activado quedan en 'preparado' (apartadas, sin entregar) en vez de
  // 'prestada'; se entregan luego desde el historial con el botón Entregar.
  const [soloPreparar, setSoloPreparar] = useState(false);
  const [comentario, setComentario] = useState("");
  const [verif, setVerif] = useState(null);       // resultado de /verificar_llave
  const [cajetin, setCajetin] = useState(null);
  const [inv, setInv] = useState(null);           // inventario esperado en cajetín
  const [pisoCheck, setPisoCheck] = useState(null); // {existe:true,codigo} | {existe:false}, feedback del ID piso tecleado a mano
  const [lote, setLote] = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [msg, setMsg] = useState(null);
  const [fichaNueva, setFichaNueva] = useState(null); // {mensaje} cuando hay que dar de alta una llave nueva
  const { alertaRevision } = useDialog();

  // autocompletados
  const [codMatches, setCodMatches] = useState([]);
  const [perMatches, setPerMatches] = useState([]);
  // Navegación con flechas + Intro en los dos desplegables.
  const navCod = useNavegacionLista(codMatches, (x) => pickPiso(x), () => setCodMatches([]));
  const navPer = useNavegacionLista(perMatches, (x) => pickPersona(x), () => setPerMatches([]));
  const codTimer = useRef(null);
  const perTimer = useRef(null);
  const idPisoTimer = useRef(null);
  const devDateRef = useRef(null);
  const codReqId = useRef(0);
  const perReqId = useRef(0);
  // Cada cambio de piso o código invalida la verificación en curso: si llega
  // una respuesta vieja (de lo que había antes de borrar/cambiar) se ignora.
  const verifReqId = useRef(0);
  const invalidarVerif = () => { verifReqId.current += 1; setVerif(null); };

  useEffect(() => {
    api.get("/departamentos").then(({ data }) => {
      let d = data || [];
      // "Personal" y "Residente" son las dos categorías genéricas de préstamo
      // (no departamentos reales); siempre van primero.
      const extra = ["Personal", "Residente"].filter((x) => !d.includes(x));
      setDeptos([...extra, ...d]);
    }).catch(() => setDeptos([]));
  }, []);

  // ---- autocompletado de piso por código ----
  // codReqId evita que una respuesta vieja (si llega tarde, p.ej. al teclear
  // rápido) pise a una más nueva con resultados desfasados o vacíos.
  const onBuscaCod = (v) => {
    setBuscaCod(v);
    clearTimeout(codTimer.current);
    if (v.trim().length < 1) { setCodMatches([]); return; }
    const miId = ++codReqId.current;
    codTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/buscar_piso", { params: { q: v.trim() } });
        if (miId === codReqId.current) setCodMatches(data || []);
      } catch { if (miId === codReqId.current) setCodMatches([]); }
    }, 220);
  };
  const pickPiso = (x) => {
    setIdPiso(String(x.id_origen));
    setBuscaCod(x.codigo);
    setCodMatches([]);
    lookupPiso(String(x.id_origen));
  };

  // ---- lookup de piso (cajetín + inventario) ----
  const lookupReqId = useRef(0);
  const lookupPiso = async (pid) => {
    const id = (pid ?? idPiso).trim();
    invalidarVerif(); setInv(null);
    const miId = ++lookupReqId.current;   // solo cuenta la respuesta de la última consulta
    if (!id) { setCajetin(null); setPisoCheck(null); return; }
    try {
      const { data } = await api.get(`/piso/${id}`);
      if (miId !== lookupReqId.current) return;
      if (data.existe) {
        setCajetin(data.cajetin);
        setInv({ total: data.llaves_actuales, prest: data.prestadas, en_cajetin: data.en_cajetin });
        setPisoCheck({ existe: true, codigo: data.codigo });
        // ID correcto: se rellena también el nombre del piso en "Buscar piso".
        setBuscaCod(data.codigo || "");
        setCodMatches([]);
      } else {
        setCajetin(null);
        setPisoCheck({ existe: false });
        setBuscaCod("");   // que no se quede el nombre de otro piso
      }
    } catch {
      if (miId !== lookupReqId.current) return;
      setCajetin(null); setPisoCheck({ existe: false });
    }
  };

  // Comprobación en vivo (con debounce) al teclear el ID piso a mano — para
  // avisar cuanto antes si es otro piso distinto del que se cree, sin
  // esperar a añadir la llave a la cesta.
  const onIdPiso = (v) => {
    setIdPiso(v);
    invalidarVerif();
    clearTimeout(idPisoTimer.current);
    setPisoCheck(null);
    if (!v.trim()) { setCajetin(null); setInv(null); return; }
    idPisoTimer.current = setTimeout(() => lookupPiso(v), 300);
  };

  // ---- verificar llave al teclear código ----
  const verificar = async () => {
    const pisoV = idPiso.trim(), codV = cod.trim();
    // Ya verificado exactamente esto (p.ej. Intro y luego salir del campo): no repetir.
    if (verif && verif.piso === pisoV && verif.cod === codV) return;
    setMsg(null); invalidarVerif();
    if (!pisoV || !codV) return;
    const miId = verifReqId.current;
    try {
      const { data } = await api.get("/verificar_llave", { params: { piso: pisoV, cod: codV } });
      if (miId !== verifReqId.current) return;   // se cambió el piso/código mientras tanto
      if (data.cajetin !== undefined) setCajetin(data.cajetin);
      if (!data.ok && data.motivo === "sin_huecos") {
        // Sin huecos libres: en vez de bloquear aquí, se ofrece dar de alta
        // una llave nueva (misma ficha que ya existe en Rutas > Preparar),
        // precargada con la configuración habitual del piso si la tiene.
        let defaults = null;
        try {
          const { data: dd } = await api.get(`/piso_defaults/${idPiso.trim()}`);
          defaults = { sugerencia: dd.sugerencia, variantes: dd.variantes };
        } catch { /* sin defaults, la ficha arranca en blanco */ }
        setFichaNueva({
          mensaje: `No hay ninguna llave libre con el código ${cod.trim()} en el piso ${data.codigo || idPiso.trim()}. ¿Confirmas que quieres dar de alta una llave nueva?`
            + (data.aviso_otro_piso ? `\n\n⚠️ ${data.aviso_otro_piso}` : ""),
          defaults,
        });
        return;
      }
      if (data.ok && data.revision) {
        // Marcada 🚩: hay que confirmar que se quiere prestar igualmente (o
        // desmarcarla en el propio aviso) antes de poder añadirla.
        const r = await pedirConfirmacionRevision(alertaRevision, data.revision,
          { accion: "prestar", etiquetaLlave: `${pisoV}-${codV}`, etiquetaPiso: data.codigo || pisoV });
        if (miId !== verifReqId.current) return;
        if (!r.seguir) {
          setVerif(null);
          setMsg({ tipo: "error", texto: `No se presta la llave ${pisoV}-${codV} (marcada para revisión).` });
          return;
        }
        setVerif({ ...data, revision: r.sigueMarcada ? data.revision : null, piso: pisoV, cod: codV,
                   desmarcar_llave: r.desmarcar_llave, desmarcar_piso: r.desmarcar_piso });
        return;
      }
      setVerif({ ...data, piso: pisoV, cod: codV });
      if (!data.ok) {
        const motivos = {
          piso_no_existe: "Ese piso no existe o está de baja.",
          ya_prestada: "Esa llave ya está prestada.",
          codigo_no_valido: "El código de llave debe ser numérico.",
        };
        setMsg({ tipo: "error", texto: motivos[data.motivo] || "No se puede prestar." });
      }
    } catch {
      setMsg({ tipo: "error", texto: "Error al verificar la llave." });
    }
  };

  // Confirmación de la ficha de "llave nueva" (ver verificar() -> sin_huecos):
  // crea la llave (recibidas) + sus componentes, y sigue como si acabara de
  // verificarse con éxito, para poder añadirla a la entrega sin más pasos.
  const confirmarFichaNueva = async (camposFicha) => {
    setFichaNueva(null);
    try {
      const { data } = await api.post("/crear_llave_ficha", { piso: idPiso.trim(), cod: cod.trim(), ...camposFicha });
      if (data.ok) {
        setVerif({ ok: true, aprender: false, nueva: true, llave_id: data.llave_id, codigo: data.codigo,
                   piso: idPiso.trim(), cod: cod.trim() });
        if (data.cajetin !== undefined) setCajetin(data.cajetin);
      } else {
        setMsg({ tipo: "error", texto: data.error || "No se pudo dar de alta la llave." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "No se pudo dar de alta la llave." });
    }
  };

  // ---- autocompletado de persona ----
  // Si ya se ha elegido categoría (Personal/Residente) en Departamento, las
  // sugerencias se limitan a esa categoría (si no, salían mezcladas).
  const onPersona = (v) => {
    setPersona(v); setPerSel(null);
    clearTimeout(perTimer.current);
    if (v.trim().length < 1) { setPerMatches([]); return; }
    const miId = ++perReqId.current;
    perTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/buscar_persona", { params: { q: v.trim() } });
        if (miId !== perReqId.current) return;
        let matches = data || [];
        if (depto === "Residente") matches = matches.filter((x) => x.tipo_persona === "alumno");
        else if (depto) matches = matches.filter((x) => x.tipo_persona === "personal");
        setPerMatches(matches);
        const exacta = coincidenciaExacta(v, matches);
        if (exacta) setPerSel(selDePersona(exacta));
      } catch { if (miId === perReqId.current) setPerMatches([]); }
    }, 220);
  };
  const selDePersona = (x) => ({ tipo_persona: x.tipo_persona, id_persona: x.id_persona, departamento: x.departamento,
                                  cuenta: cuentaDeResultado(x) });
  const pickPersona = (x) => {
    setPersona(x.nombre);
    setPerSel(selDePersona(x));
    if (x.departamento) setDepto(x.departamento);
    setPerMatches([]);
  };
  // Botón "✕" para pasar a otra persona: persona/depto/devolución se
  // mantienen entre altas (para prestar varias llaves seguidas a la misma
  // persona sin re-teclear), así que hace falta una forma explícita de
  // vaciarlos al cambiar de destinatario.
  const limpiarPersona = () => { setPersona(""); setPerSel(null); setDev(""); setDepto(""); setTipoPrestamo("provisional"); };

  // ---- añadir a la cesta ----
  const anadir = () => {
    setMsg(null);
    if (!verif || !verif.ok) { setMsg({ tipo: "error", texto: "Verifica una llave válida primero (Intro en el código)." }); return; }
    if (verif.cod !== cod.trim() || verif.piso !== idPiso.trim()) {
      setMsg({ tipo: "error", texto: "El piso o el código han cambiado: vuelve a verificarlo (Intro en el código)." });
      return;
    }
    if (lote.some((x) => x.llave_id === verif.llave_id)) {
      setMsg({ tipo: "error", texto: "Esa llave ya está en la prelista." });
      return;
    }
    if (dev && esToIso(dev) && esToIso(dev) < hoyIso()) {
      setMsg({ tipo: "error", texto: "La devolución estimada no puede ser anterior a hoy." });
      return;
    }
    setLote((prev) => [...prev, {
      llave_id: verif.llave_id,
      aprender: verif.aprender,
      nueva: !!(verif.aprender || verif.nueva),
      revision: !!verif.revision,
      desmarcar_llave: !!verif.desmarcar_llave,
      desmarcar_piso: !!verif.desmarcar_piso,
      cod: verif.cod,
      piso_codigo: verif.codigo,
      id_piso: idPiso.trim(),
      tipo_persona: perSel?.tipo_persona || null,
      id_persona: perSel?.id_persona || null,
      persona: persona.trim() || null,
      devol: esToIso(dev) || null,
      tipo_prestamo: tipoPrestamo,
      comentario: comentario.trim() || null,
      solo_preparar: soloPreparar,
    }]);
    // Limpia solo llave/comentario: persona, departamento y devolución se
    // mantienen para poder prestar varias llaves seguidas a la misma persona
    // sin re-teclearlos (usa el botón ✕ de Persona para pasar a otra).
    setCod(""); setVerif(null); setComentario("");
  };

  const quitar = (i) => setLote((prev) => prev.filter((_, idx) => idx !== i));

  const confirmar = async () => {
    setMsg(null);
    if (!lote.length) return;
    try {
      const { data } = await api.post("/prestar", { items: lote });
      if (data.ok) {
        const nPrep = lote.filter((x) => x.solo_preparar).length;
        setMsg({ tipo: "ok", texto: nPrep === lote.length
          ? `Preparadas ${data.creados} llave(s), pendientes de entregar.`
          : `Registradas ${data.creados} llave(s)${nPrep ? ` (${nPrep} solo preparadas, pendientes de entregar)` : ""}.` });
        setLote([]);
        onCambio?.();
      } else {
        setMsg({ tipo: "error", texto: data.error || "No se pudo confirmar." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "Error de conexión." });
    }
  };

  return (
    <div className="card op-col">
      <div className="colhead presta"><span className="ic">↗</span><b>Prestar</b>
        <span className="fecha">📅 {new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}</span>
      </div>

      {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}

      {/* buscar piso por código */}
      <div className="field" style={{ position: "relative" }}>
        <label>Buscar piso (código)</label>
        <input value={buscaCod} placeholder="Ej: AB1.1B — al elegir, rellena el ID"
               onChange={(e) => onBuscaCod(e.target.value)} onKeyDown={navCod.onKeyDown}
               onBlur={() => setTimeout(() => setCodMatches([]), 150)} autoComplete="off" />
        {codMatches.length > 0 && (
          <div className="deplist" ref={navCod.listaRef}>
            {codMatches.map((x, i) => (
              <div {...navCod.opcion(i)} key={x.id_origen} onMouseDown={(e) => { e.preventDefault(); pickPiso(x); }}>
                {x.codigo} <span className="muted">· ID {x.id_origen}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="row3">
        <div className="field">
          <label>ID piso</label>
          <input className={`mono ${pisoCheck ? (pisoCheck.existe ? "input-ok" : "input-error") : ""}`}
                 value={idPiso} placeholder="33" inputMode="numeric"
                 onChange={(e) => onIdPiso(e.target.value)} onBlur={() => lookupPiso()} autoComplete="off" />
          {pisoCheck && (
            <div className={`field-feedback ${pisoCheck.existe ? "ok" : "error"}`}>
              {pisoCheck.existe ? `✓ ${pisoCheck.codigo}` : "✗ no encontrado"}
            </div>
          )}
        </div>
        <div className="field"><label>Código llave</label>
          <input className="mono" value={cod} placeholder="1234"
                 onChange={(e) => { setCod(e.target.value); setMsg(null); invalidarVerif(); }} onBlur={verificar}
                 onKeyDown={(e) => e.key === "Enter" && verificar()} autoComplete="off" />
        </div>
        <div className={`cajbox ${cajetin ? "" : "vacio"}`}>
          <span className="lbl">Cajetín</span><span className="val">{cajetin ?? "—"}</span>
        </div>
      </div>

      {inv && (
        <div className="inv">
          🗝️ En cajetín ahora: <b>{inv.en_cajetin}</b> de {inv.total} (prestadas {inv.prest})
        </div>
      )}
      {verif && verif.ok && (verif.aprender || verif.nueva ? (
        <div className="fb nueva" role="alert">
          <span className="fb-ic">⚠️</span>
          <div>
            <b>LLAVE NUEVA · {verif.piso}-{verif.cod}</b>
            <div>
              {verif.aprender
                ? <>El código <b>{verif.cod}</b> no existía en el piso <b>{verif.codigo}</b>: se registrará en una llave sin código de ese piso al confirmar la entrega.</>
                : <>Se acaba de dar de alta la llave <b>{verif.cod}</b> en el piso <b>{verif.codigo}</b>.</>}
            </div>
            <small>Comprueba que el número de la etiqueta es correcto antes de añadirla.</small>
          </div>
        </div>
      ) : (
        <div className="fb ok">✓ Llave {verif.cod} del piso {verif.codigo}, disponible.</div>
      ))}

      <div className="row">
        <div className="field">
          <label>Devolución estimada</label>
          <div style={{ display: "flex", gap: 6, position: "relative" }}>
            <input className="mono" style={{ flex: 1 }} value={dev} placeholder="dd/mm/aaaa" inputMode="numeric"
                   onChange={(e) => setDev(e.target.value)} autoComplete="off" />
            <button type="button" className="btn btn-mini" title="Elegir día en el calendario"
                    onClick={() => { const el = devDateRef.current; if (el?.showPicker) el.showPicker(); else el?.focus(); }}>
              📅
            </button>
            <input ref={devDateRef} type="date" value={esToIso(dev) || ""} min={hoyIso()}
                   onChange={(e) => setDev(e.target.value ? isoToEs(e.target.value) : "")}
                   style={{ position: "absolute", opacity: 0, width: 0, height: 0, border: "none", padding: 0, pointerEvents: "none" }} />
          </div>
        </div>
        <div className="field">
          <label>Tipo</label>
          <div className="seg-buttons">
            <button type="button" className={tipoPrestamo === "provisional" ? "on" : ""}
                    onClick={() => setTipoPrestamo("provisional")}>Provisional</button>
            <button type="button" className={tipoPrestamo === "fija" ? "on" : ""}
                    onClick={() => setTipoPrestamo("fija")}>Fija</button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="field" style={{ position: "relative" }}><label>Persona</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input style={{ flex: 1 }} value={persona} placeholder="Nombre (personal o residente)"
                   onChange={(e) => onPersona(e.target.value)} onKeyDown={navPer.onKeyDown}
                   onBlur={() => setTimeout(() => setPerMatches([]), 150)} autoComplete="off" />
            {(persona || perSel || dev || depto) && (
              <button type="button" className="btn btn-mini" onClick={limpiarPersona}
                      title="Limpiar persona/devolución (para pasar a otra persona)"
                      style={{ color: "#dc2626", borderColor: "#fca5a5" }}>
                ✕
              </button>
            )}
          </div>
          {perMatches.length > 0 && (
            <div className="deplist" ref={navPer.listaRef}>
              {perMatches.map((x, i) => (
                <div {...navPer.opcion(i)} key={i} onMouseDown={(e) => { e.preventDefault(); pickPersona(x); }}>
                  {x.nombre} <span className="muted">· {x.tipo_persona === "personal" ? `personal${x.puesto ? ` (${x.puesto})` : ""}` : "residente"} · ID {x.id_persona}</span>
                </div>
              ))}
            </div>
          )}
          <AvisoCuentaPersona texto={persona} cuenta={perSel?.cuenta || null} />
        </div>
        <div className="field"><label>Departamento</label>
          {/* "Personal"/"Residente" son solo un filtro para el buscador de
              Persona (ver onPersona); no tocan el campo Nombre. */}
          <select value={depto} onChange={(e) => setDepto(e.target.value)}>
            <option value="">—</option>
            {deptos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Comentario (opcional, para esta llave)</label>
        <input value={comentario} placeholder="Ej: llave de repuesto"
               onChange={(e) => setComentario(e.target.value)} autoComplete="off" />
      </div>

      <div className="seg-buttons" style={{ marginBottom: 8 }}>
        <button type="button" className={soloPreparar ? "on" : ""}
                title="Activado: las llaves que añadas quedan preparadas (sin entregar), no prestadas"
                onClick={() => setSoloPreparar((v) => !v)}>
          📦 Preparar (sin entregar)
        </button>
      </div>

      <button className="btn" style={{ width: "100%" }} onClick={anadir}>+ Añadir a la entrega</button>

      <div className="sec">Prelista<span className="count">{lote.length}</span></div>
      <div className="prelista">
        {lote.map((x, i) => (
          <div className="pl-item" key={i}>
            <span className="mono">{x.piso_codigo} · {x.cod}{x.nueva && <span className="pl-nueva" title="Llave nueva">🆕 nueva</span>}{x.revision && <span className="pl-revision" title="Marcada para revisión">🚩 revisar</span>}</span>
            <span className="muted">
              {x.persona || "—"}{x.tipo_prestamo === "fija" ? " · Fija" : ""}{x.devol ? ` · ${isoToEs(x.devol)}` : ""}{x.comentario ? ` · 💬 ${x.comentario}` : ""}{x.solo_preparar ? " · 📦 Preparar" : ""}
            </span>
            <button className="btn btn-mini" onClick={() => quitar(i)}>✕</button>
          </div>
        ))}
        {lote.length === 0 && <div className="empty">Añade llaves para entregarlas juntas.</div>}
      </div>

      {fichaNueva && (
        <FichaLlaveModal mensaje={fichaNueva.mensaje} defaults={fichaNueva.defaults} peligroso
                        onCancel={() => setFichaNueva(null)} onConfirmar={confirmarFichaNueva} />
      )}

      <button className="btn btn-primary" style={{ width: "100%", marginTop: 10 }}
              disabled={!lote.length} onClick={confirmar}>Confirmar entrega</button>
    </div>
  );
}

// Traduce el resultado de /verificar_devolucion a {tono, texto} para pintar
// el campo de código. "reaparecida" y "aprender" no son errores de verdad
// (son flujos válidos que se resuelven al devolver), así que van en ámbar,
// no en rojo — el rojo se deja solo para lo que de verdad no se puede hacer.
function feedbackDevolucion(codCheck) {
  if (!codCheck) return null;
  if (codCheck.ok) {
    return codCheck.aprender
      ? { tono: "warn", texto: "⚠ Código nuevo en este piso — se aprenderá al devolver" }
      : { tono: "ok", texto: `✓ Prestada a ${codCheck.persona || "(sin nombre)"}` };
  }
  const motivos = {
    reaparecida: { tono: "warn", texto: "⚠ Constaba perdida — se preguntará si reintroducirla" },
    piso_no_existe: { tono: "error", texto: "✗ Piso no válido" },
    codigo_no_valido: { tono: "error", texto: "✗ El código debe ser numérico" },
    no_prestada: { tono: "error", texto: "✗ Esa llave no consta como prestada" },
  };
  return motivos[codCheck.motivo] || { tono: "error", texto: "✗ No se puede devolver" };
}

/* ======================================================================= */
/*  DEVOLVER (tecleo rápido)                                                */
/* ======================================================================= */
function Devolver({ onCambio }) {
  const [idPiso, setIdPiso] = useState("");
  const [cod, setCod] = useState("");
  const [info, setInfo] = useState(null);
  const [devueltas, setDevueltas] = useState([]);
  const [reap, setReap] = useState(null); // llave reaparecida
  const [msg, setMsg] = useState(null);
  const [pisoCheck, setPisoCheck] = useState(null); // {existe:true,codigo} | {existe:false}
  const [codCheck, setCodCheck] = useState(null);   // resultado de /verificar_devolucion
  const idPisoTimer = useRef(null);
  const { confirmar, alertaRevision } = useDialog();

  // Mismo aviso en vivo que en Prestar: se escribe el ID a mano (aquí no hay
  // buscador por código), así que conviene confirmar cuanto antes de qué
  // piso se trata antes de devolver la llave equivocada.
  const onIdPiso = (v) => {
    setIdPiso(v);
    clearTimeout(idPisoTimer.current);
    setPisoCheck(null);
    if (!v.trim()) return;
    idPisoTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/piso/${v.trim()}`);
        setPisoCheck(data.existe ? { existe: true, codigo: data.codigo } : { existe: false });
      } catch { setPisoCheck({ existe: false }); }
    }, 300);
  };

  // Comprobación en vivo del código de llave (sin devolver de verdad): dice
  // si se puede devolver, de quién es, o por qué no antes de darle a Enter.
  useEffect(() => {
    if (!idPiso.trim() || !cod.trim()) { setCodCheck(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/verificar_devolucion", { params: { piso: idPiso.trim(), cod: cod.trim() } });
        setCodCheck(data);
      } catch { setCodCheck({ ok: false }); }
    }, 300);
    return () => clearTimeout(t);
  }, [idPiso, cod]);

  // revisionElegida: tras confirmar el aviso 🚩, {desmarcar_llave, desmarcar_piso}.
  const devolver = async (e, revisionElegida = null) => {
    e?.preventDefault();
    setMsg(null); setReap(null);
    if (!idPiso.trim() || !cod.trim()) return;
    try {
      const { data } = await api.post("/devolver", { piso: idPiso.trim(), cod: cod.trim(),
                                                     confirmado_revision: !!revisionElegida, ...(revisionElegida || {}) });
      if (data.motivo === "revision") {
        // Marcada 🚩: el backend no la devuelve hasta confirmar aquí.
        const r = await pedirConfirmacionRevision(alertaRevision, data.revision,
          { accion: "devolver", etiquetaLlave: `${data.id_piso}-${cod.trim()}`, etiquetaPiso: data.codigo });
        if (r.seguir) devolver(null, { desmarcar_llave: r.desmarcar_llave, desmarcar_piso: r.desmarcar_piso });
        else setMsg({ tipo: "error", texto: `No se ha devuelto la llave ${data.id_piso}-${cod.trim()} (marcada para revisión).` });
        return;
      }
      if (data.motivo === "reaparecida") {
        setReap({ llave_id: data.llave_id, mov_pendiente: data.mov_pendiente,
                  comentario: data.comentario_perdida, codigo: data.codigo, cod: cod.trim() });
        setCod("");
        return;
      }
      if (!data.ok) {
        const m = {
          piso_no_existe: "Ese piso no existe o está de baja.",
          no_prestada: "Esa llave no consta como prestada.",
        };
        setMsg({ tipo: "error", texto: m[data.motivo] || "No se pudo devolver." });
        return;
      }
      setInfo({
        mov_id: data.mov_id, codigo: data.codigo, cajetin: data.cajetin, persona: data.persona,
        desde: data.desde, en_cajetin: data.en_cajetin, pendientes: data.pendientes || [],
      });
      setDevueltas((prev) => [{ mov_id: data.mov_id, cod: cod.trim(), persona: data.persona, piso: data.codigo }, ...prev]);
      setCod("");
      onCambio?.();
    } catch {
      setMsg({ tipo: "error", texto: "Error al devolver la llave." });
    }
  };

  // Deshace la última devolución hecha por error (p.ej. código equivocado):
  // vuelve el préstamo a 'prestada'. Solo tiene sentido justo después de
  // devolver, mientras el aviso sigue en pantalla.
  const deshacer = async () => {
    if (!info?.mov_id) return;
    if (!(await confirmar(`¿Deshacer la devolución de la llave del piso ${info.codigo}? Volverá a quedar como prestada.`))) return;
    try {
      const { data } = await api.post("/deshacer_devolucion", { mov_id: info.mov_id });
      if (data.ok) {
        setInfo(null);
        setDevueltas((prev) => prev.filter((d) => d.mov_id !== info.mov_id));
        setMsg({ tipo: "ok", texto: "Devolución deshecha: la llave vuelve a constar como prestada." });
        onCambio?.();
      } else {
        setMsg({ tipo: "error", texto: data.error || "No se pudo deshacer." });
      }
    } catch (err) {
      setMsg({ tipo: "error", texto: err.response?.data?.error || "No se pudo deshacer." });
    }
  };

  const confirmarReaparece = async () => {
    if (!reap) return;
    try {
      await api.post("/reaparece", { llave_id: reap.llave_id });
      setMsg({ tipo: "ok", texto: `Llave ${reap.cod} reintroducida al inventario.` });
      setReap(null);
      onCambio?.();
    } catch {
      setMsg({ tipo: "error", texto: "No se pudo reintroducir la llave." });
    }
  };

  const fbCod = feedbackDevolucion(codCheck);

  return (
    <div className="card op-col">
      <div className="colhead devu"><span className="ic">↙</span><b>Devolver</b></div>

      {msg && <div className={`alert ${msg.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msg.texto}</div>}

      <form onSubmit={devolver} className="row">
        <div className="field">
          <label>ID piso</label>
          <input className={`mono ${pisoCheck ? (pisoCheck.existe ? "input-ok" : "input-error") : ""}`}
                 value={idPiso} placeholder="1" inputMode="numeric"
                 onChange={(e) => onIdPiso(e.target.value)} autoComplete="off" />
          {pisoCheck && (
            <div className={`field-feedback ${pisoCheck.existe ? "ok" : "error"}`}>
              {pisoCheck.existe ? `✓ ${pisoCheck.codigo}` : "✗ no encontrado"}
            </div>
          )}
        </div>
        <div className="field">
          <label>Código llave</label>
          <input className={`mono ${fbCod ? `input-${fbCod.tono}` : ""}`} value={cod} placeholder="501"
                 onChange={(e) => setCod(e.target.value)} autoComplete="off" />
          {fbCod && <div className={`field-feedback ${fbCod.tono}`}>{fbCod.texto}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <button className="btn btn-primary" type="submit">Devolver</button>
        </div>
      </form>

      {reap && (
        <div className="bigbox warn">
          <b>⚠ Llave reaparecida</b>
          <p>La llave {reap.cod} del piso {reap.codigo} constaba como perdida
            {reap.comentario ? ` (${reap.comentario})` : ""}. ¿Reintroducir al inventario?</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-success" onClick={confirmarReaparece}>Sí, reintroducir</button>
            <button className="btn" onClick={() => setReap(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {info && (
        <div className="bigbox">
          <div className="bb-line">Devuelta la llave del piso <b>{info.codigo}</b></div>
          <div className="cajbox-big">
            <span className="lbl">Cajetín</span>
            <span className="val">{info.cajetin ?? "—"}</span>
          </div>
          <div className="bb-line">En cajetín ahora: <b>{info.en_cajetin}</b></div>
          <div className="bb-line">Era de <b>{info.persona}</b>{info.desde ? ` · desde ${isoToEs(info.desde)}` : ""}</div>
          {info.pendientes.length > 0 && (
            <div className="bb-pend">
              <b>Sigue teniendo:</b>
              <ul>
                {info.pendientes.map((p, i) => (
                  <li key={i}>{p.piso} · {p.cod ?? "—"} (cajetín {p.cajetin ?? "—"})</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-mini" onClick={deshacer} title="Deshacer esta devolución">↩ Deshacer</button>
          </div>
        </div>
      )}

      <div className="sec">Devueltas en sesión<span className="count d">{devueltas.length}</span></div>
      <div className="dev-list">
        {devueltas.map((d, i) => (
          <div className="dl-item" key={i}>
            <span className="mono">{d.piso} · {d.cod}</span>
            <span className="muted">{d.persona}</span>
          </div>
        ))}
        {devueltas.length === 0 && <div className="empty">Teclea una llave devuelta para verla aquí.</div>}
      </div>
    </div>
  );
}

/* ======================================================================= */
/*  LEDGER (historial + filtros + reclamación)                             */
/* ======================================================================= */
function Ledger() {
  const [rows, setRows] = useState([]);
  const [estado, setEstado] = useState("todos"); // 'prestada' | 'todos'
  const [soloVenc, setSoloVenc] = useState(false);
  const [soloRevisar, setSoloRevisar] = useState(false);
  // Filtro por día: salidas o devoluciones de ese día (o de un rango).
  const [dia, setDia] = useState("");
  const [conRango, setConRango] = useState(false);
  const [diaHasta, setDiaHasta] = useState("");
  const [filtros, setFiltros] = useState({});
  const [filtrosKey, setFiltrosKey] = useState(0); // fuerza remount de los inputs al limpiar
  const [sel, setSel] = useState(null);          // detalle abierto
  const [recl, setRecl] = useState(null);        // modal de reclamación
  const [revisarInfo, setRevisarInfo] = useState(null); // modal de marcar revisión
  const [infoLlaveId, setInfoLlaveId] = useState(null); // modal de info/componentes de la llave
  const filTimer = useRef(null);
  const { avisar, confirmar, exito, alertaRevision } = useDialog();
  const [anular, setAnular] = useState(null); // fila del préstamo que se va a anular
  const [perdida, setPerdida] = useState(null); // fila del préstamo cuya llave se ha perdido
  const marcarEncontrada = async (r) => {
    if (!(await confirmar(`¿Marcar la llave ${r.pid}-${r.llave ?? "sin nº"} (${r.piso}) como ENCONTRADA? Volverá a quedar prestada a ${r.nombre || "—"}.`))) return;
    try {
      const { data } = await api.post("/prestamo_encontrada", { mov_id: r.mov_id });
      if (!data.ok) return avisar(data.error || "No se pudo marcar como encontrada.");
      await cargar(); avisarCambioResumen();
      exito(`La llave ${r.pid}-${r.llave ?? "sin nº"} (${r.piso}) vuelve prestada a ${r.nombre || "—"}.`);
    } catch (err) { avisar(err.response?.data?.error || "No se pudo marcar como encontrada."); }
  };

  // Devolver desde el historial (vale también para llaves sin número).
  const devolverFila = async (r, revisionElegida = null) => {
    const etq = `${r.pid}-${r.llave ?? "sin nº"} (${r.piso})`;
    if (!revisionElegida && !(await confirmar(`¿Registrar la devolución de la llave ${etq} que tenía ${r.nombre || "—"}?`))) return;
    try {
      const { data } = await api.post("/devolver_prestamo", { mov_id: r.mov_id, confirmado_revision: !!revisionElegida, ...(revisionElegida || {}) });
      if (data.motivo === "revision") {
        const rv = await pedirConfirmacionRevision(alertaRevision, data.revision,
          { accion: "devolver", etiquetaLlave: `${r.pid}-${r.llave ?? "sin nº"}`, etiquetaPiso: r.piso });
        if (rv.seguir) devolverFila(r, { desmarcar_llave: rv.desmarcar_llave, desmarcar_piso: rv.desmarcar_piso });
        return;
      }
      if (!data.ok) { avisar(data.error || "No se pudo devolver."); return; }
      await cargar(); avisarCambioResumen();
      exito(`Devuelta la llave ${etq} que tenía ${r.nombre || "—"}.`);
    } catch (err) { avisar(err.response?.data?.error || "No se pudo devolver."); }
  };

  const cargar = useCallback(async () => {
    const params = { estado, ...filtros };
    if (soloVenc) params.vencidas = "1";
    if (dia) {
      params.dia_desde = dia;
      if (conRango && diaHasta) params.dia_hasta = diaHasta;
    }
    try {
      const { data } = await api.get("/ledger", { params });
      setRows(data.rows || []);
    } catch { setRows([]); }
  }, [estado, soloVenc, filtros, dia, conRango, diaHasta]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => onActualizarPanel(cargar), [cargar]);

  const onFiltro = (campo, valor) => {
    clearTimeout(filTimer.current);
    filTimer.current = setTimeout(() => {
      setFiltros((prev) => {
        const n = { ...prev };
        if (valor.trim()) n[campo] = valor.trim(); else delete n[campo];
        return n;
      });
    }, 250);
  };

  // Chips de "Persona": mismo filtro tipo_persona que ya entiende el servidor.
  const setTipoPersona = (v) => setFiltros((prev) => {
    const n = { ...prev };
    if (v) n.tipo_persona = v; else delete n.tipo_persona;
    return n;
  });

  const abrirDetalle = async (mov_id, modo = "ver") => {
    try {
      const { data } = await api.get(`/llave_detalle/${mov_id}`);
      if (data.ok) setSel({ mov_id, modo, ...data });
    } catch {}
  };

  // 'preparado' -> 'prestada' (la llave se entrega ahora): se confirma antes,
  // y después se avisa y se recarga el historial (la fila pasa a Prestada).
  const entregar = async (r) => {
    const etq = `${r.pid}-${r.llave ?? "?"} (${r.piso})`;
    if (!(await confirmar(`¿Entregar ahora la llave ${etq} a ${r.nombre || "—"}?\n\n` +
      "Pasará de Preparada a Prestada, con fecha de salida de ahora."))) return;
    try {
      const { data } = await api.post("/entregar_prestamo", { mov_id: r.mov_id });
      if (data.ok) {
        await cargar(); avisarCambioResumen();
        exito(`Entregada la llave ${etq} a ${r.nombre || "—"}. Ya consta como prestada en el historial.`);
      } else avisar(data.error || "No se pudo entregar.");
    } catch (err) {
      avisar(err.response?.data?.error || "No se pudo entregar.");
    }
  };

  // Cancelar una llave preparada sin entregar: queda libre otra vez.
  const cancelarPreparacion = async (r) => {
    const deRuta = !!r.ruta_id;
    const ok = await confirmar(
      `¿Cancelar la preparación de la llave ${r.pid}-${r.llave ?? "?"} (${r.piso}) para ${r.nombre || "—"}?\n\n` +
      (deRuta
        ? `Es de la ruta nº ${r.ruta_id}: esa línea volverá a quedar pendiente de preparar.`
        : "La llave quedará libre en el cajetín, como si no se hubiera preparado."));
    if (!ok) return;
    try {
      const { data } = await api.post("/cancelar_preparacion", { mov_id: r.mov_id });
      if (data.ok) { cargar(); avisarCambioResumen(); } else avisar(data.error || "No se pudo cancelar.");
    } catch (err) { avisar(err.response?.data?.error || "No se pudo cancelar."); }
  };

  const abrirReclamacion = async (mov_id) => {
    try {
      const { data } = await api.post("/reclamar", { mov_id });
      if (data.ok) setRecl({ mov_id, ...data });
      cargar();
    } catch {}
  };

  const columnas = [
    ["piso", "Piso"], ["id", "ID"], ["llave", "Llave"],
    ["id_persona", "ID Persona"], ["nombre", "Nombre"], ["tipo", "Tipo"], ["estado_f", "Estado"],
    ["salida", "Salida"], ["devol", "Dev. estimada"], ["entrada", "Devuelta"],
  ];
  // Anchos FIJOS (la tabla usa table-layout: fixed): así no cambian al
  // escribir en los filtros y todo cabe sin desplazarse a la derecha.
  const anchos = { piso: "14%", id: "6%", llave: "7%", id_persona: "9%", nombre: "17%", tipo: "8%",
                   estado_f: "9%", salida: "9%", devol: "9%", entrada: "9%" };

  const rowsMostrados = soloRevisar ? rows.filter((r) => r.revisar) : rows;
  // Llaves distintas dentro de lo filtrado (una llave puede salir en varios registros del historial).
  const llavesDistintas = new Set(rowsMostrados.map((r) => r.llave_id)).size;
  // Llegan TODOS los préstamos (sin tope en el servidor); aquí se pintan de 100 en 100.
  const { pagina, setPagina, totalPaginas, paginaRows } = usePaginacion(rowsMostrados, [rows, soloRevisar]);

  return (
    <div className="card ledger">
      <div className="lg-controls">
        <div className="lg-estados">
          <button className={estado === "prestada" ? "on" : ""} data-tone={tonoEstado("prestada")} onClick={() => setEstado("prestada")}>Prestadas ahora</button>
          <button className={estado === "preparado" ? "on" : ""} data-tone={tonoEstado("preparado")} onClick={() => setEstado("preparado")}>📦 Por entregar</button>
          <button className={estado === "todos" ? "on" : ""} data-tone="gray" onClick={() => setEstado("todos")}>Todas</button>
        </div>
        <div className={`lg-dia ${dia ? "on" : ""}`} title="Préstamos que salieron o se devolvieron ese día (o en ese rango)">
          <span className="lg-dia-ic">📅</span>
          <input type="date" value={dia} aria-label={conRango ? "Desde" : "Día"} onChange={(e) => setDia(e.target.value)} />
          {conRango && (
            <>
              <span className="lg-dia-sep">→</span>
              <input type="date" value={diaHasta} aria-label="Hasta" min={dia || undefined} onChange={(e) => setDiaHasta(e.target.value)} />
            </>
          )}
          <button type="button" className={`lg-dia-rango ${conRango ? "on" : ""}`} onClick={() => setConRango((v) => !v)}
                  title={conRango ? "Quitar el rango (un solo día)" : "Elegir un rango de días"}>
            Rango
          </button>
          {dia && (
            <button type="button" className="lg-dia-x" title="Quitar filtro de día"
                    onClick={() => { setDia(""); setDiaHasta(""); setConRango(false); }}>✕</button>
          )}
        </div>
        <button className={`lg-venc ${soloVenc ? "on" : ""}`} onClick={() => setSoloVenc((v) => !v)}>⏰ Solo vencidas</button>
        <button className={`lg-venc ${soloRevisar ? "on" : ""}`} onClick={() => setSoloRevisar((v) => !v)}>🚩 Solo marcadas para revisión</button>
        <div className="lg-total" title="Se actualiza con los filtros activos">
          <div className="lg-total-main">
            <span className="lg-total-ic">🗝️</span>
            <b key={llavesDistintas}>{llavesDistintas.toLocaleString("es-ES")}</b>
            <span className="lg-total-lbl">{llavesDistintas === 1 ? "llave" : "llaves"}</span>
          </div>
          <div className="lg-total-sub">
            <b>{rowsMostrados.length.toLocaleString("es-ES")}</b>
            <span>{rowsMostrados.length === 1 ? "registro" : "registros"}</span>
          </div>
        </div>
        <button className="btn" onClick={() => { setFiltros({}); setFiltrosKey((k) => k + 1); setDia(""); setDiaHasta(""); setConRango(false); }}>Limpiar filtros</button>
      </div>

      <div className="lg-persona">
        <span className="lg-persona-t">👤 Persona</span>
        <div className="seg-buttons">
          <button className={!filtros.tipo_persona ? "on" : ""} data-tone="gray" onClick={() => setTipoPersona("")}>Todas</button>
        </div>
        <div className="lg-persona-grupo">
          <span className="lg-persona-g">Personal</span>
          <div className="seg-buttons">
            {[["personal", "Todo", "blue"], ["personal_asistenta", "Asistentas", "green"],
              ["personal_supervisora", "Supervisoras", "amber"], ["personal_otros", "Otros", "gray"]].map(([v, t, tone]) => (
              <button key={v} className={filtros.tipo_persona === v ? "on" : ""} data-tone={tone} onClick={() => setTipoPersona(v)}>{t}</button>
            ))}
          </div>
        </div>
        <div className="seg-buttons">
          <button className={filtros.tipo_persona === "alumno" ? "on" : ""} data-tone="blue" onClick={() => setTipoPersona("alumno")}>🎓 Residentes</button>
          <button className={filtros.tipo_persona === "ninguno" ? "on" : ""} data-tone="red" onClick={() => setTipoPersona("ninguno")}>❔ Sin identificar</button>
        </div>
      </div>

      <div className="tablewrap">
        <table className="lg lg-fija">
          <colgroup>
            {columnas.map(([f]) => <col key={f} style={{ width: anchos[f] }} />)}
            <col style={{ width: 128 }} />
          </colgroup>
          <thead>
            <tr>
              {columnas.map(([f, t]) => (
                <th key={f}>
                  {t}
                  <input key={`${f}-${filtrosKey}`} placeholder={["salida", "devol", "entrada"].includes(f) ? "dd/mm/aaaa" : "filtrar"} onChange={(e) => onFiltro(f, e.target.value)} />
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paginaRows.map((r) => (
              <tr key={r.mov_id} className={`datarow ${r.estado === "prestada" && r.devol && String(r.devol).slice(0, 10) < new Date().toISOString().slice(0, 10) ? "venc" : ""} ${r.revisar ? "revisar" : ""}`}>
                <td>{r.piso} <span className="muted">c.{r.cajetin ?? "—"}</span></td>
                <td>{r.pid}</td>
                <td className="mono">{r.llave ?? "—"}</td>
                <td>
                  {r.id_persona ?? "—"}
                  {r.tipo_persona && (
                    <div className="muted" style={{ fontSize: 11, color: "#94a3b8" }}>
                      {r.tipo_persona === "alumno" ? "residente" : `personal${r.puesto ? ` · ${r.puesto}` : ""}`}
                    </div>
                  )}
                </td>
                <td>{r.nombre || "—"}</td>
                <td>{r.tipo === "fija" ? "Fija" : "Provisional"}</td>
                <td><Badge tone={tonoEstado(r.estado)}>{r.estado}</Badge></td>
                <td>{isoToEs(r.salida)}</td>
                <td>{isoToEs(r.devol)}</td>
                <td>{isoToEs(r.entrada) || "—"}</td>
                <td className="lg-acc"><div className="lg-grid">
                  {r.estado === "preparado" && (
                    <>
                      <button className="btn btn-mini btn-primary" onClick={() => entregar(r)} title="Entregar: pasa a prestada">Entregar</button>
                      <button className="btn btn-mini acc-ambar" onClick={() => cancelarPreparacion(r)} title="Cancelar la preparación (la llave queda libre)">✕</button>
                    </>
                  )}
                  <button className="btn btn-mini" onClick={() => abrirDetalle(r.mov_id, "ver")} title="Ver detalle">👁️</button>
                  {r.estado === "prestada" && (
                    <>
                      <button className="btn btn-mini acc-verde" onClick={() => devolverFila(r)} title="Devolver: registrar que la han devuelto">↩️</button>
                      <button className="btn btn-mini acc-rojo" onClick={() => setPerdida(r)} title="Perdida: quien la tenía la ha perdido">⛔</button>
                      <button className="btn btn-mini btn-editar" onClick={() => abrirDetalle(r.mov_id, "editar")} title="Editar préstamo e historial">✏️</button>
                      <button className="btn btn-mini btn-reclamar" onClick={() => abrirReclamacion(r.mov_id)} title="Reclamar devolución">🔔</button>
                      {!r.ruta_id && (
                        <button className="btn btn-mini acc-rojo" onClick={() => setAnular(r)} title="Anular: préstamo registrado por error">🚫</button>
                      )}
                    </>
                  )}
                  {r.estado === "perdida" && (
                    <button className="btn btn-mini acc-verde" onClick={() => marcarEncontrada(r)} title="Encontrada: vuelve prestada a quien la perdió">✅</button>
                  )}
                  <button className="btn btn-mini" onClick={() => setInfoLlaveId(r.llave_id)} title="Ver/editar componentes de esta llave">ℹ️</button>
                  <button className={`btn btn-mini btn-revisar ${r.revisar ? "on" : ""}`}
                          title={r.revisar ? "Marcada para revisión" : "Marcar para revisión"}
                          onClick={() => setRevisarInfo({ llave_id: r.llave_id, piso_id: r.pid, codigo: r.llave, piso: r.piso, valor: !r.revisar, nota: "", alcance: "llave" })}>
                    {r.revisar ? "✅" : "🚩"}
                  </button>
                </div></td>
              </tr>
            ))}
            {rowsMostrados.length === 0 && <tr><td colSpan={11} className="empty">Sin resultados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Paginador pagina={pagina} totalPaginas={totalPaginas} onCambiar={setPagina} />

      {sel && <DetalleModal sel={sel} onClose={() => setSel(null)} onGuardado={cargar} />}
      {perdida && <PerdidaPrestamoModal fila={perdida} onClose={() => setPerdida(null)}
                                        onHecho={async () => {
                                          const r = perdida; setPerdida(null); await cargar(); avisarCambioResumen();
                                          exito(`La llave ${r.pid}-${r.llave ?? "sin nº"} (${r.piso}) queda como PERDIDA por ${r.nombre || "—"}.`);
                                        }} />}
      {anular && <AnularPrestamoModal fila={anular} onClose={() => setAnular(null)}
                                      onHecho={() => { setAnular(null); cargar(); avisarCambioResumen(); }} />}
      {recl && <ReclamacionModal recl={recl} onClose={() => setRecl(null)} />}
      {revisarInfo && <RevisarLlaveModal info={revisarInfo} onClose={() => setRevisarInfo(null)} onGuardado={cargar} />}
      {infoLlaveId && <InfoLlaveModal llaveId={infoLlaveId} onClose={() => setInfoLlaveId(null)} onGuardado={cargar} />}
    </div>
  );
}

// Anular un préstamo registrado POR ERROR (llave o persona equivocada): se
// borra como si no hubiera existido y queda constancia en el historial del
// piso. Ventana roja con motivo obligatorio: no se puede deshacer.
// Historial > ⛔: quien tenía la llave la ha perdido. La llave se da de baja
// y el préstamo se cierra como "perdida" (deja de contar como prestada).
function PerdidaPrestamoModal({ fila, onClose, onHecho }) {
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const enviar = async () => {
    setGuardando(true); setError(null);
    try {
      const { data } = await api.post("/prestamo_perdido", { mov_id: fila.mov_id, motivo: motivo.trim() || null });
      if (data.ok) onHecho(); else setError(data.error || "No se pudo marcar como perdida.");
    } catch (err) { setError(err.response?.data?.error || "No se pudo marcar como perdida."); }
    finally { setGuardando(false); }
  };
  return (
    <div className="modal-back modal-back-peligro" onMouseDown={onClose}>
      <div className="modal modal-sm modal-peligro" onMouseDown={(e) => e.stopPropagation()}>
        <h3>⛔ Llave perdida</h3>
        <p style={{ marginTop: 0 }}>
          La llave <b>{fila.pid}-{fila.llave ?? "sin nº"}</b> ({fila.piso}) que tenía <b>{fila.nombre || "—"}</b> se dará
          de <b>baja como perdida</b> y el préstamo quedará cerrado como perdido.
        </p>
        <p className="muted" style={{ fontSize: 13 }}>Si luego aparece, se puede reactivar desde Inventario (↩️) o al devolverla.</p>
        <label>Motivo (opcional)</label>
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus placeholder="p.ej. la ha perdido, se la robaron…"
               onKeyDown={(e) => e.key === "Enter" && enviar()} />
        {error && <div className="alert alert-error" style={{ marginTop: 8 }}>{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={enviar} disabled={guardando}>{guardando ? "Guardando…" : "Sí, marcar como perdida"}</button>
        </div>
      </div>
    </div>
  );
}

function AnularPrestamoModal({ fila, onClose, onHecho }) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const anular = async () => {
    setError(null);
    if (!motivo.trim()) { setError("Indica el motivo."); return; }
    setGuardando(true);
    try {
      const { data } = await api.post("/anular_prestamo", { mov_id: fila.mov_id, motivo: motivo.trim() });
      if (data.ok) onHecho(); else setError(data.error || "No se pudo anular.");
    } catch (err) { setError(err.response?.data?.error || "No se pudo anular."); }
    finally { setGuardando(false); }
  };

  return (
    <div className="modal-back modal-back-peligro" onMouseDown={onClose}>
      <div className="modal modal-sm modal-peligro" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🚫 Anular préstamo</h3>
        <p style={{ marginTop: 0 }}>
          Llave <b>{fila.pid}-{fila.llave ?? "?"}</b> · {fila.piso}<br />
          Prestada a <b>{fila.nombre || "—"}</b>{fila.salida ? ` el ${isoToEs(fila.salida)}` : ""}.
        </p>
        <div className="alert alert-error">
          Úsalo solo si el préstamo <b>se registró por error</b> (llave o persona equivocada). Se borrará como si
          no hubiera existido, con su historial, y <b>no se puede deshacer</b>. Si la llave se prestó de verdad y ha
          vuelto, usa <b>Devolver</b>.
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <label>Motivo *</label>
        <textarea rows={2} value={motivo} maxLength={300} autoFocus placeholder="Ej: me equivoqué de llave, era la 12…"
                  onChange={(e) => setMotivo(e.target.value)} />
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={anular} disabled={guardando}>
            {guardando ? "Anulando…" : "Sí, anular préstamo"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Marcar/desmarcar revisión manual de una llave: "esta llave" (todos sus
// componentes en detalle_llaves) o "todo el piso" (piso_ajustes, la misma
// bandera que usa el 🚩 del Panel dinámico).
// El propio botón 🚩 que abre esto ya decide si es marcar o resolver (según
// el estado actual de esa llave), así que aquí no hace falta un checkbox
// redundante para repetir esa misma decisión — solo elegir el ámbito y,
// si se está marcando, el motivo.
function RevisarLlaveModal({ info, onClose, onGuardado }) {
  const [nota, setNota] = useState(info.nota || "");
  const [alcance, setAlcance] = useState(info.alcance || "llave");

  const guardar = async () => {
    if (info.valor) {
      // Marcar: hay que elegir a qué se aplica.
      if (alcance === "piso") {
        await api.post("/piso_ajuste", { piso_id: info.piso_id, campo: "revisar", valor: true, nota });
      } else {
        await api.post("/llave_revisar", { llave_id: info.llave_id, valor: true, nota });
      }
    } else {
      // Resolver: da igual de qué tipo era la marca (de la llave, del piso,
      // o de las dos) — se quitan ambas, sin tener que elegir.
      await Promise.all([
        api.post("/llave_revisar", { llave_id: info.llave_id, valor: false }),
        api.post("/piso_ajuste", { piso_id: info.piso_id, campo: "revisar", valor: false }),
      ]);
    }
    onGuardado();
    onClose();
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{info.valor ? "🚩 Marcar para revisión" : "✅ Resolver revisión"} — llave {info.codigo ?? "s/c"} ({info.piso})</h3>
        {info.valor && (
          <>
            <div style={{ display: "flex", gap: 14, margin: "10px 0" }}>
              <label><input type="radio" name="revisar-alcance" checked={alcance === "llave"} onChange={() => setAlcance("llave")} /> Solo esta llave</label>
              <label><input type="radio" name="revisar-alcance" checked={alcance === "piso"} onChange={() => setAlcance("piso")} /> Todo el piso</label>
            </div>
            <label>Motivo</label>
            <input value={nota} onChange={(e) => setNota(e.target.value)} autoFocus />
          </>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar}>{info.valor ? "Marcar" : "Resolver"}</button>
        </div>
      </div>
    </div>
  );
}

function DetalleModal({ sel, onClose, onGuardado }) {
  // 'ver' = consulta de solo lectura (comentario, seguridad, historial,
  // reclamaciones). 'editar' = lo mismo + editar préstamo/comentario/
  // historial — son botones distintos en el Ledger (ver useLedgerAcciones),
  // para no mezclar "mirar" con "tocar algo" en un único flujo.
  const modo = sel.modo || "ver";
  const editable = modo === "editar";
  const [coment, setComent] = useState(sel.comentarios || "");
  const [hist, setHist] = useState([]);
  const [verHist, setVerHist] = useState(false);
  const [editHistId, setEditHistId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  // En modo editar, el historial se carga solo (no hace falta pedirlo aparte
  // para poder corregir una fila); en modo ver se sigue pidiendo a mano.
  useEffect(() => {
    if (editable) {
      api.get(`/historial/${sel.mov_id}`).then(({ data }) => { setHist(data || []); setVerHist(true); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edición del préstamo activo en sí (persona/devolución estimada/tipo),
  // con historial de cambios (lo registra /editar_prestamo).
  const [editPersona, setEditPersona] = useState(sel.nombre_crudo || "");
  const [editDevol, setEditDevol] = useState(sel.fecha_devolucion_estimada ? isoToEs(sel.fecha_devolucion_estimada) : "");
  const [editTipo, setEditTipo] = useState(sel.tipo_prestamo || "provisional");
  const [editNota, setEditNota] = useState("");
  // Cuenta enlazada (personal / residente) del préstamo: al cambiar el nombre
  // hay que elegirla del listado; si no, queda como texto libre (sin cuenta).
  // Nunca se queda la cuenta anterior con el nombre de otra persona.
  const [editCuenta, setEditCuenta] = useState(sel.cuenta || null);
  const [perMatchesEd, setPerMatchesEd] = useState([]);
  const perTimerEd = useRef(null);
  const perReqEd = useRef(0);
  const { confirmar } = useDialog();
  const onEditPersona = (v) => {
    setEditPersona(v);
    // Si se vuelve al nombre original, vuelve su cuenta; si se escribe otra
    // cosa distinta de la persona elegida, deja de estar enlazado.
    if (v.trim() === (sel.nombre_crudo || "").trim()) setEditCuenta(sel.cuenta || null);
    else if (editCuenta && v.trim() !== (editCuenta.nombre || "").trim()) setEditCuenta(null);
    clearTimeout(perTimerEd.current);
    if (!v.trim()) { setPerMatchesEd([]); return; }
    const miId = ++perReqEd.current;
    perTimerEd.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/buscar_persona", { params: { q: v.trim() } });
        if (miId !== perReqEd.current) return;
        setPerMatchesEd(data || []);
        const exacta = coincidenciaExacta(v, data);
        if (exacta) setEditCuenta(cuentaDeResultado(exacta));
      } catch { if (miId === perReqEd.current) setPerMatchesEd([]); }
    }, 220);
  };
  const elegirPersonaEd = (x) => {
    setEditPersona(x.nombre);
    setEditCuenta(cuentaDeResultado(x));
    setPerMatchesEd([]);
  };
  const navPerEd = useNavegacionLista(perMatchesEd, elegirPersonaEd, () => setPerMatchesEd([]));
  const nombreCambiado = editPersona.trim() !== (sel.nombre_crudo || "").trim();
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [msgEdicion, setMsgEdicion] = useState(null);
  const editDevDateRef = useRef(null);

  const guardarComent = async () => {
    await api.post("/comentario", { mov_id: sel.mov_id, texto: coment });
    onGuardado?.();
  };

  const guardarEdicionPrestamo = async () => {
    if (editDevol && esToIso(editDevol) && esToIso(editDevol) < hoyIso()) {
      setMsgEdicion({ tipo: "error", texto: "La devolución estimada no puede ser anterior a hoy." });
      return;
    }
    if (nombreCambiado && !editCuenta && !(await confirmar(
      `Vas a cambiar el nombre a "${editPersona.trim()}" SIN elegir a nadie del listado.\n\n`
      + "El préstamo quedará como texto libre (sin enlazar a ninguna cuenta de personal ni residente). "
      + "Si es alguien de personal o un residente, cancela y elígelo del listado.\n\n¿Guardar como texto libre?"))) return;
    setGuardandoEdicion(true);
    setMsgEdicion(null);
    try {
      const { data } = await api.post("/editar_prestamo", {
        mov_id: sel.mov_id, persona: editPersona.trim(),
        cuenta: editCuenta ? { tipo_persona: editCuenta.tipo_persona, id_persona: editCuenta.id_persona } : null,
        devol: esToIso(editDevol) || null, tipo_prestamo: editTipo,
        nota: editNota.trim() || null,
      });
      if (data.ok) {
        setEditNota("");
        setMsgEdicion({ tipo: "ok", texto: data.cambios > 0 ? "Cambios guardados." : "No había cambios." });
        onGuardado?.();
      } else {
        setMsgEdicion({ tipo: "error", texto: data.error || "No se pudo guardar." });
      }
    } catch (err) {
      setMsgEdicion({ tipo: "error", texto: err.response?.data?.error || "No se pudo guardar." });
    } finally {
      setGuardandoEdicion(false);
    }
  };
  const cargarHist = async () => {
    const { data } = await api.get(`/historial/${sel.mov_id}`);
    setHist(data || []); setVerHist(true);
  };

  // Edición directa de una fila de historial ya existente (para corregir un
  // dato mal escrito, no solo consultarlo). La propia edición queda anotada
  // como una fila nueva de historial (lo hace el backend).
  const abrirEdicionHist = (h) => {
    setEditHistId(h.id);
    setEditForm({
      fecha: String(h.fecha || "").slice(0, 16),
      fechaOriginal: h.fecha || "",
      campo: h.campo || "",
      valor_antes: h.valor_antes || "",
      valor_despues: h.valor_despues || "",
      nota: h.nota || "",
    });
  };
  const cancelarEdicionHist = () => { setEditHistId(null); setEditForm(null); };
  const guardarEdicionHist = async () => {
    // Si no se ha tocado la fecha, se manda tal cual (con sus segundos
    // originales) en vez del recorte a minutos que usa el <input>.
    const fechaCambiada = editForm.fecha !== String(editForm.fechaOriginal || "").slice(0, 16);
    try {
      await api.post("/historial/editar", {
        historial_id: editHistId,
        fecha: fechaCambiada ? editForm.fecha : editForm.fechaOriginal,
        campo: editForm.campo, valor_antes: editForm.valor_antes,
        valor_despues: editForm.valor_despues, nota: editForm.nota,
      });
      cancelarEdicionHist();
      cargarHist();
    } catch {
      // se deja el formulario abierto para reintentar
    }
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>Llave {sel.codigo ?? "—"} · piso {sel.piso}</h3>
        <div className="muted" style={{ marginBottom: 8 }}>
          Estado: <Badge tone={tonoEstado(sel.estado)}>{sel.estado}</Badge> · Salida {isoToEs(sel.fecha_salida)}{sel.fecha_entrada ? ` · Devuelta ${isoToEs(sel.fecha_entrada)}` : ""}
        </div>
        {sel.seguridad && (
          <div className="fb ok">🔒 Seguridad{sel.seg_proveedor ? ` · ${sel.seg_proveedor}` : ""}</div>
        )}
        {editable && sel.estado === "prestada" && (
          <>
            <hr className="modal-hr" />
            <div className="section-title" style={{ margin: "0 0 4px", fontSize: 15 }}>✏️ Editar préstamo</div>
            {msgEdicion && <div className={`alert ${msgEdicion.tipo === "ok" ? "alert-ok" : "alert-error"}`}>{msgEdicion.texto}</div>}
            <label>Persona</label>
            <div style={{ position: "relative" }}>
              <input value={editPersona} onChange={(e) => onEditPersona(e.target.value)} autoComplete="off"
                     onKeyDown={(e) => { navPerEd.onKeyDown(e); }}
                     onBlur={() => setTimeout(() => setPerMatchesEd([]), 150)}
                     placeholder="Escribe y elige del listado" />
              {perMatchesEd.length > 0 && (
                <div className="deplist" ref={navPerEd.listaRef}>
                  {perMatchesEd.map((x, i) => (
                    <div {...navPerEd.opcion(i)} key={`${x.tipo_persona}-${x.id_persona}`} onMouseDown={(e) => { e.preventDefault(); elegirPersonaEd(x); }}>
                      {x.nombre} <span className="muted">· {x.tipo_persona === "personal" ? `personal${x.puesto ? ` (${x.puesto})` : ""}` : "residente"} · ID {x.id_persona}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <AvisoCuentaPersona texto={editPersona} cuenta={editCuenta} />
            <label>Devolución estimada</label>
            <div style={{ display: "flex", gap: 6, position: "relative" }}>
              <input className="mono" style={{ flex: 1 }} value={editDevol} placeholder="dd/mm/aaaa" inputMode="numeric"
                     onChange={(e) => setEditDevol(e.target.value)} autoComplete="off" />
              <button type="button" className="btn btn-mini" title="Elegir día en el calendario"
                      onClick={() => { const el = editDevDateRef.current; if (el?.showPicker) el.showPicker(); else el?.focus(); }}>
                📅
              </button>
              <input ref={editDevDateRef} type="date" value={esToIso(editDevol) || ""} min={hoyIso()}
                     onChange={(e) => setEditDevol(e.target.value ? isoToEs(e.target.value) : "")}
                     style={{ position: "absolute", opacity: 0, width: 0, height: 0, border: "none", padding: 0, pointerEvents: "none" }} />
            </div>
            <label>Tipo</label>
            <div className="seg-buttons">
              <button type="button" className={editTipo === "provisional" ? "on" : ""} onClick={() => setEditTipo("provisional")}>Provisional</button>
              <button type="button" className={editTipo === "fija" ? "on" : ""} onClick={() => setEditTipo("fija")}>Fija</button>
            </div>
            <label>Motivo del cambio (opcional, queda en el historial)</label>
            <input value={editNota} onChange={(e) => setEditNota(e.target.value)} />
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-primary" onClick={guardarEdicionPrestamo} disabled={guardandoEdicion}>
                {guardandoEdicion ? "Guardando…" : "Guardar cambios del préstamo"}
              </button>
            </div>
            <hr className="modal-hr" />
          </>
        )}
        <label>Comentarios</label>
        {editable ? (
          <textarea value={coment} onChange={(e) => setComent(e.target.value)} style={{ minHeight: 80 }} />
        ) : (
          <p className="muted" style={{ whiteSpace: "pre-line" }}>{coment || "—"}</p>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {editable && <button className="btn btn-primary" onClick={guardarComent}>Guardar comentario</button>}
          {!verHist && <button className="btn" onClick={cargarHist}>Ver historial</button>}
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>Cerrar</button>
        </div>
        {verHist && (
          <div className="hist">
            {hist.map((h) => (
              <div className="hist-row" key={h.id}>
                {editable && editHistId === h.id ? (
                  <div className="hist-edit">
                    <input type="datetime-local" value={editForm.fecha}
                           onChange={(e) => setEditForm((p) => ({ ...p, fecha: e.target.value }))} />
                    <input value={editForm.campo} placeholder="campo"
                           onChange={(e) => setEditForm((p) => ({ ...p, campo: e.target.value }))} />
                    <input value={editForm.valor_antes} placeholder="valor antes"
                           onChange={(e) => setEditForm((p) => ({ ...p, valor_antes: e.target.value }))} />
                    <input value={editForm.valor_despues} placeholder="valor después"
                           onChange={(e) => setEditForm((p) => ({ ...p, valor_despues: e.target.value }))} />
                    <input value={editForm.nota} placeholder="nota"
                           onChange={(e) => setEditForm((p) => ({ ...p, nota: e.target.value }))} />
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      <button className="btn btn-mini btn-primary" onClick={guardarEdicionHist}>Guardar</button>
                      <button className="btn btn-mini" onClick={cancelarEdicionHist}>Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="muted">{isoToEs(h.fecha)}</span> · {h.campo}
                    {h.valor_antes ? ` : ${h.valor_antes} → ${h.valor_despues}` : (h.valor_despues ? ` : ${h.valor_despues}` : "")}
                    {h.nota ? ` · ${h.nota}` : ""}
                    {editable && (
                      <button className="btn btn-mini" style={{ marginLeft: 8 }} onClick={() => abrirEdicionHist(h)} title="Editar esta fila">✏️</button>
                    )}
                  </>
                )}
              </div>
            ))}
            {hist.length === 0 && <div className="empty">Sin cambios registrados.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ReclamacionModal({ recl, onClose }) {
  const [msg, setMsg] = useState(recl.mensaje || "");
  const copiar = () => navigator.clipboard?.writeText(msg);
  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>Reclamar devolución</h3>
        <div className="muted" style={{ marginBottom: 8 }}>
          {recl.telefono ? `Teléfono: ${recl.telefono}` : "Sin teléfono de contacto en ficha."}
          {recl.dias_retraso != null ? ` · ${recl.dias_retraso} día(s)` : ""}
        </div>
        <label>Mensaje (editable, listo para copiar)</label>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} style={{ minHeight: 120 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={copiar}>Copiar mensaje</button>
          {recl.telefono && <button className="btn" onClick={() => { window.location.href = `sms:${recl.telefono}?body=${encodeURIComponent(msg)}`; }}>Abrir SMS</button>}
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
